/**
 * Track 2: frame-time guard. Feeds on per-frame durations and steps the graphics quality down when the device
 * cannot keep up (rolling average over a window, then a cooldown so it never flaps). It never steps back up on its
 * own: a stable low setting is better than oscillating. Pure – the caller applies the returned quality.
 */
import type { Quality } from './quality.ts';

const ORDER: Quality[] = ['high', 'medium', 'low'];

export class FrameGuard {
  private sum = 0;
  private n = 0;
  private cooldown = 0;

  constructor(
    private level: Quality,
    private readonly opts: { budgetMs?: number; window?: number; cooldownFrames?: number } = {},
  ) {}

  get quality(): Quality { return this.level; }

  /** Returns the new quality when it just stepped down, else null. Ignores absurd frames (tab switches). */
  push(dtMs: number): Quality | null {
    if (!(dtMs > 0) || dtMs > 250) return null;
    if (this.cooldown > 0) { this.cooldown--; return null; }
    this.sum += dtMs; this.n++;
    const window = this.opts.window ?? 90;
    if (this.n < window) return null;
    const avg = this.sum / this.n;
    this.sum = 0; this.n = 0;
    const idx = ORDER.indexOf(this.level);
    if (avg > (this.opts.budgetMs ?? 24) && idx < ORDER.length - 1) {
      this.level = ORDER[idx + 1]!;
      this.cooldown = this.opts.cooldownFrames ?? 120;
      return this.level;
    }
    return null;
  }
}
